import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

export interface CompanyPlanInfo {
  id: string
  plan_id: string
  plan_name: string
  system_id: string | null
  system_name: string
  system_code: string
  status: string
  features: any
  start_date: string
  end_date?: string
  is_trial: boolean
  trial_started_at?: string
  trial_expires_at?: string
}

export function useCompanyPlans(companyId?: string) {
  return useQuery({
    queryKey: ['company-plans-admin', companyId],
    queryFn: async () => {
      if (!companyId) return []

      // Use optional embeds (LEFT JOIN) to avoid losing rows when embed fails
      const { data, error } = await supabase
        .from('company_system_plans')
        .select(`
          id,
          plan_id,
          system_id,
          status,
          start_date,
          end_date,
          is_trial,
          trial_started_at,
          trial_expires_at,
          plans:plans (
            id,
            name,
            features,
            system_id,
            systems:systems (
              id,
              name,
              code
            )
          ),
          systems:system_id (
            id,
            name,
            code
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'active')

      if (error) throw error

      // Fallback: fetch plans missing due to RLS on embed
      const missingPlanIds = (data || [])
        .filter((item: any) => !item.plans && item.plan_id)
        .map((item: any) => item.plan_id)

      let fallbackPlansMap = new Map<string, any>()
      if (missingPlanIds.length > 0) {
        const { data: fallbackPlans, error: fallbackError } = await supabase
          .from('plans')
          .select('id, name, features, system_id')
          .in('id', missingPlanIds)

        if (fallbackError) {
          console.error('Fallback fetch for plans failed:', fallbackError)
        } else {
          for (const p of fallbackPlans || []) {
            fallbackPlansMap.set(p.id, p)
          }
        }
      }

      // Fallback: fetch systems missing due to RLS on embed
      const missingSystemIds = (data || [])
        .filter((item: any) => {
          const plan = item.plans || fallbackPlansMap.get(item.plan_id)
          return !item.systems && !plan?.systems && (item.system_id || plan?.system_id)
        })
        .map((item: any) => {
          const plan = item.plans || fallbackPlansMap.get(item.plan_id)
          return item.system_id || plan?.system_id
        })
        .filter(Boolean)

      let fallbackSystemsMap = new Map<string, any>()
      if (missingSystemIds.length > 0) {
        const { data: fallbackSystems, error: fallbackError } = await supabase
          .from('systems')
          .select('id, name, code')
          .in('id', missingSystemIds)

        if (fallbackError) {
          console.error('Fallback fetch for systems failed:', fallbackError)
        } else {
          for (const s of fallbackSystems || []) {
            fallbackSystemsMap.set(s.id, s)
          }
        }
      }

      // Map to expected format with fallbacks
      const mappedData: CompanyPlanInfo[] = (data || []).map((item: any) => {
        const plan = item.plans || fallbackPlansMap.get(item.plan_id)
        const system = item.systems || plan?.systems || fallbackSystemsMap.get(item.system_id || plan?.system_id)

        return {
          id: item.id,
          plan_id: item.plan_id,
          plan_name: plan?.name || 'Unknown Plan',
          system_id: item.system_id || plan?.system_id || system?.id || null,
          system_name: system?.name || 'Unknown System',
          system_code: system?.code || 'unknown',
          status: item.status,
          features: plan?.features || {},
          start_date: item.start_date,
          end_date: item.end_date,
          is_trial: item.is_trial || false,
          trial_started_at: item.trial_started_at,
          trial_expires_at: item.trial_expires_at
        }
      })

      console.log('🔍 [useCompanyPlans(admin)] Final result:', mappedData)
      return mappedData
    },
    enabled: !!companyId,
    staleTime: 30_000,
    retry: 1,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev ?? [],
  })
}

export function useUserPlanAccess(userId?: string, companyId?: string) {
  const { data: plans, isLoading: plansLoading } = useCompanyPlans(companyId)
  
  return useQuery({
    queryKey: ['user-plan-access', userId, companyId, plans?.map(p => p.plan_id).join(',') || 'no-plans'],
    queryFn: async () => {
      if (!userId || !companyId) {
        console.log('🚫 useUserPlanAccess: Missing userId or companyId')
        return []
      }

      if (!plans) {
        console.log('🚫 useUserPlanAccess: No plans available yet')
        return []
      }

      console.log('🔍 useUserPlanAccess: Checking access for user:', userId, 'company:', companyId)
      console.log('📋 Available plans:', plans.length)

      // Check if user is member of the company
      const { data: membership, error } = await supabase
        .from('user_companies')
        .select('role')
        .eq('user_id', userId)
        .eq('company_id', companyId)
        .maybeSingle()

      if (error) {
        console.error('❌ useUserPlanAccess: Error fetching membership:', error)
        return []
      }

      if (!membership) {
        console.log('🚫 useUserPlanAccess: User is not a member of this company')
        return []
      }

      console.log('✅ useUserPlanAccess: User membership confirmed, role:', membership.role)

      // Return all systems from active plans
      const systemAccess = plans.map(plan => ({
        system_id: plan.system_id,
        system_name: plan.system_name,
        system_code: plan.system_code,
        plan_name: plan.plan_name,
        user_role: membership.role
      }))

      console.log('🎯 useUserPlanAccess: Final system access:', systemAccess)
      return systemAccess
    },
    enabled: !!userId && !!companyId && plans !== undefined,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  })
}

export function useValidateUserLimit(companyId?: string) {
  const { data: plans, isLoading: plansLoading } = useCompanyPlans(companyId)
  
  return useQuery({
    queryKey: ['user-limit', companyId, plans?.map(p => p.plan_id).join(',') || 'no-plans'],
    queryFn: async () => {
      if (!companyId) return { canAddUser: true, limit: 0, current: 0 }

      let maxUsers = 10 // More generous default fallback

      console.log('🔍 useValidateUserLimit: Checking user limits for company:', companyId)
      console.log('📊 Plans available:', plans?.length || 0)
      console.log('📋 Plans data:', plans)

      // Priority 1: Check new plan system (wait for plans to load)
      if (plans && plans.length > 0) {
        maxUsers = plans.reduce((max, plan) => {
          const features = plan.features as any || {}
          const limits = features.limits || {}
          const toggles = features.toggles || {}
          
          console.log(`📋 Plan ${plan.plan_name || 'Unknown'}:`, { 
            rawFeatures: plan.features,
            parsedFeatures: features,
            limits,
            toggles,
            users_max: limits.users_max,
            users_max_type: typeof limits.users_max,
            unlimited_users: toggles.unlimited_users 
          })
          
          // Priority 1: Check for unlimited users toggle first
          if (toggles.unlimited_users === true) {
            console.log('♾️ Using unlimited toggle: 999999')
            return Math.max(max, 999999)
          }
          
          // Priority 2: Check numeric limit with improved validation
          const userLimit = limits.users_max
          const numericLimit = typeof userLimit === 'string' ? Number(userLimit) : userLimit
          
          if (userLimit !== undefined && userLimit !== null && 
              (typeof userLimit === 'number' || (typeof userLimit === 'string' && !isNaN(Number(userLimit)))) &&
              numericLimit > 0) {
            console.log(`✅ Using numeric limit: ${numericLimit} (original: ${userLimit}, type: ${typeof userLimit})`)
            return Math.max(max, numericLimit)
          }
          
          // Log for debugging when no limit is found
          console.log(`⚠️ Plan ${plan.plan_name} - no valid user limit found:`, {
            'limits.users_max': limits.users_max,
            'typeof': typeof limits.users_max,
            'parsed_number': Number(limits.users_max),
            'isNaN': isNaN(Number(limits.users_max))
          })
          
          return max
        }, 10) // Improved fallback
      } else if (plans && plans.length === 0) {
        // Plans loaded but empty - check legacy modules
        console.log('📦 No plans found, checking legacy modules...')
        const { data: modules, error: moduleError } = await supabase
          .from('company_modules')
          .select('seats')
          .eq('company_id', companyId)
          .eq('status', 'active')

        if (!moduleError && modules && modules.length > 0) {
          maxUsers = modules.reduce((max, module) => {
            return Math.max(max, module.seats || 1)
          }, 1)
          console.log('📦 Using legacy module seats:', maxUsers)
        }
      } else {
        console.log('⏳ Plans still loading, using temporary fallback:', maxUsers)
      }

      // Count current users
      const { count, error } = await supabase
        .from('user_companies')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId)

      if (error) throw error

      const currentUsers = count || 0
      
      console.log(`📊 Final limit calculation: ${currentUsers}/${maxUsers} users`)

      return {
        canAddUser: currentUsers < maxUsers,
        limit: maxUsers,
        current: currentUsers
      }
    },
    enabled: !!companyId && plans !== undefined,
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes to reduce recalculations
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  })
}